import { useState } from "react";
import { KeyRound, Plus, Trash2, Loader2, Copy } from "lucide-react";
import axios, { API } from "@/lib/apiClient";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

export const M2mKeysPanel = ({ keys, onKeysChanged }) => {
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [revealedKey, setRevealedKey] = useState(null);
  const [revokingId, setRevokingId] = useState(null);

  const createMasterKey = async () => {
    if (!label.trim()) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/m2m-keys/master`, { label: label.trim() });
      setRevealedKey(data.key);
      setLabel("");
      onKeysChanged();
      toast.success("Master M2M key generated - copy it now.");
    } catch {
      toast.error("Could not generate master key.");
    } finally {
      setBusy(false);
    }
  };

  const revokeKey = async (keyId) => {
    setRevokingId(keyId);
    try {
      await axios.delete(`${API}/admin/m2m-keys/${keyId}`);
      onKeysChanged();
      toast.success("Key revoked.");
    } catch {
      toast.error("Could not revoke key.");
    } finally {
      setRevokingId(null);
    }
  };

  const copyKey = async () => {
    try {
      await navigator.clipboard.writeText(revealedKey);
      toast.success("Copied to clipboard.");
    } catch {
      toast.error("Could not copy. Copy manually.");
    }
  };

  return (
    <div className="rounded-lg border border-surface-border bg-surface-card p-6" data-testid="m2m-keys-panel">
      <div className="mb-1 flex items-center gap-2">
        <KeyRound className="h-4 w-4 text-openai-cyan" />
        <h2 className="font-heading text-xl font-bold text-white">Remote Machine (M2M) Keys</h2>
      </div>
      <p className="mb-4 text-xs text-slate-500">
        Your master key is authorized across every client's nodes. Client keys (self-generated in their own Settings) are scoped to their own account only.
      </p>

      {revealedKey && (
        <div className="mb-4 rounded-md border border-warning-amber/50 bg-warning-amber/5 p-3" data-testid="m2m-revealed-key">
          <p className="mb-1 text-xs font-bold uppercase tracking-wide text-warning-amber">Copy this key now - it will not be shown again</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 truncate text-xs text-white">{revealedKey}</code>
            <Button size="sm" onClick={copyKey} className="gap-1 bg-warning-amber text-obsidian hover:bg-warning-amber/90" data-testid="m2m-copy-key-btn">
              <Copy className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}

      {keys.length > 0 && (
        <div className="mb-4 space-y-2" data-testid="m2m-keys-list">
          {keys.map((k) => (
            <div key={k.id} className="flex items-center justify-between rounded-md border border-surface-border bg-obsidian p-3" data-testid={`m2m-key-row-${k.id}`}>
              <div>
                <p className="text-sm font-semibold text-white">{k.label} <span className="ml-1 text-[10px] uppercase text-slate-500">{k.scope}</span></p>
                <p className="text-xs text-slate-500">{k.owner_email} &middot; {k.prefix}_&hellip; {k.revoked_at ? "(revoked)" : ""}</p>
              </div>
              {!k.revoked_at && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={revokingId === k.id}
                  onClick={() => revokeKey(k.id)}
                  className="gap-1 border-sovereign/40 text-sovereign hover:bg-sovereign/10"
                  data-testid={`m2m-revoke-key-${k.id}`}
                >
                  {revokingId === k.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2">
        <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Master key label" data-testid="m2m-new-master-label-input" />
        <Button onClick={createMasterKey} disabled={busy} className="gap-2 bg-openai-cyan text-obsidian hover:bg-openai-cyan/90" data-testid="m2m-generate-master-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Generate Master Key
        </Button>
      </div>
    </div>
  );
};
